import { useRef, useState } from 'react';
import PageLayout from '../components/PageLayout';
import { useCompany } from '../context/CompanyContext';
import api from '../api';
import './Dashboard.css';
import './AiNotice.css';

function escapeHtml(str) {
  return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function apiErrorMessage(err) {
  return err.response?.data?.detail || err.message;
}

function AiNotice() {
  const { selectedCompanyId } = useCompany();
  const fileInputRef = useRef(null);

  const [noticeType, setNoticeType] = useState('Income Tax');
  const [noticeText, setNoticeText] = useState('');
  const [fileName, setFileName] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadNote, setUploadNote] = useState(null);

  const [analysing, setAnalysing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [analyseError, setAnalyseError] = useState(null);

  const [draftOpen, setDraftOpen] = useState(false);
  const [draftLoading, setDraftLoading] = useState(false);
  const [draftLetter, setDraftLetter] = useState('');
  const [draftError, setDraftError] = useState(null);
  const [copied, setCopied] = useState(false);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadNote(null);
    setFileName(file.name);

    const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
    if (!isPdf) {
      setUploadNote('Image OCR is not supported yet — paste the notice text below instead.');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    setUploading(true);
    api
      .post('/ai-notice/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then((res) => setNoticeText(res.data.extracted_text))
      .catch((err) => setUploadNote(apiErrorMessage(err)))
      .finally(() => setUploading(false));
  };

  const handleAnalyse = () => {
    if (!noticeText.trim() || !selectedCompanyId) return;
    setAnalysing(true);
    setAnalyseError(null);
    setAnalysis(null);
    api
      .post('/ai-notice/analyse', {
        notice_text: noticeText,
        notice_type: noticeType,
        company_id: selectedCompanyId,
      })
      .then((res) => setAnalysis(res.data))
      .catch((err) => setAnalyseError(apiErrorMessage(err)))
      .finally(() => setAnalysing(false));
  };

  const openDraftModal = () => {
    if (!analysis) return;
    setDraftOpen(true);
    setDraftLoading(true);
    setDraftError(null);
    setDraftLetter('');
    setCopied(false);
    api
      .post('/ai-notice/draft-response', {
        company_id: selectedCompanyId,
        notice_type: analysis.notice_type,
        section: analysis.section,
        plain_english: analysis.plain_english,
      })
      .then((res) => setDraftLetter(res.data.letter))
      .catch((err) => setDraftError(apiErrorMessage(err)))
      .finally(() => setDraftLoading(false));
  };

  const handleCopyLetter = () => {
    navigator.clipboard.writeText(draftLetter);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownloadWord = () => {
    const html = `<html><head><meta charset="utf-8"></head><body><pre style="font-family:Calibri;white-space:pre-wrap;">${escapeHtml(
      draftLetter
    )}</pre></body></html>`;
    const blob = new Blob(['﻿', html], { type: 'application/msword' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'response-letter.doc';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadAnalysis = () => {
    if (!analysis) return;
    const text = [
      'Krosifin — AI Notice Analysis',
      '',
      `Notice Type: ${analysis.notice_type}`,
      `Section: ${analysis.section}`,
      `Severity: ${analysis.severity}`,
      '',
      'Plain English:',
      analysis.plain_english,
      '',
      'Action Plan:',
      ...analysis.action_plan.map((step, i) => `${i + 1}. ${step}`),
      '',
      `Deadline: ${analysis.deadline}`,
      `Consequence of non-response: ${analysis.consequence}`,
      '',
      'Documents needed:',
      ...analysis.documents_needed.map((d) => `- ${d}`),
    ].join('\n');
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'krosifin-ai-notice-analysis.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  const deadlineDays = analysis ? parseInt(analysis.deadline, 10) : null;
  const deadlineUrgent = Number.isFinite(deadlineDays) && deadlineDays < 30;

  return (
    <PageLayout>
      <div className="page-title-row">
        <h1 className="page-title">AI Notice Interpreter</h1>
        <p className="page-subtitle">
          Upload any Income Tax or GST notice — get plain English explanation + recommended action
        </p>
      </div>

      <div className="dash-card">
        <div className="field-group">
          <span className="field-label">Notice type</span>
          <select className="field-select" value={noticeType} onChange={(e) => setNoticeType(e.target.value)}>
            <option value="Income Tax">Income Tax</option>
            <option value="GST">GST</option>
          </select>
        </div>

        <div className="upload-zone" onClick={() => fileInputRef.current?.click()}>
          <div className="upload-icon">📄⬆</div>
          <div className="upload-text">
            {uploading ? 'Extracting text...' : 'Click to upload IT / GST notice (PDF or image)'}
          </div>
          <div className="upload-subtext">
            Supports: IT u/s 143(1), 148, 139(9), 270A · GST notices ASMT-10, DRC-01 · Any demand / SCN
          </div>
          {fileName && <div className="upload-subtext">Selected: {fileName}</div>}
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,image/*"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
        </div>

        {uploadNote && <p className="dash-error">{uploadNote}</p>}

        <div className="field-group">
          <span className="field-label">Or paste notice text directly</span>
          <textarea
            className="field-input ai-textarea"
            rows={6}
            value={noticeText}
            onChange={(e) => setNoticeText(e.target.value)}
            placeholder="Paste the full text of the notice here..."
          />
        </div>

        {analyseError && <p className="dash-error">{analyseError}</p>}

        <div className="dash-btn-row">
          <button
            className="dash-btn dash-btn-orange"
            type="button"
            disabled={!noticeText.trim() || analysing}
            onClick={handleAnalyse}
          >
            {analysing ? 'Analysing with Claude...' : 'Analyse Notice'}
          </button>
        </div>
      </div>

      {analysis && (
        <div className="dash-card">
          <div className="ai-analysis-header">
            <div className="ai-robot-icon">🤖</div>
            <div style={{ flex: 1 }}>
              <div className="ai-analysis-title">Krosifin AI Analysis Complete</div>
              <div className="ai-analysis-sub">{analysis.section}</div>
            </div>
            <span
              className={`dash-pill ${
                analysis.severity === 'High'
                  ? 'dash-pill-red'
                  : analysis.severity === 'Low'
                  ? 'dash-pill-green'
                  : 'dash-pill-amber'
              }`}
            >
              {analysis.severity === 'Low' ? 'Routine' : 'Action Required'}
            </span>
          </div>

          <div className="ai-section plain">
            <p className="ai-section-title">What this notice says (plain English):</p>
            <p style={{ margin: 0 }}>{analysis.plain_english}</p>
          </div>

          <div className="ai-section recommend">
            <p className="ai-section-title">Recommended action:</p>
            <ol>
              {analysis.action_plan.map((step, i) => (
                <li key={i}>{step}</li>
              ))}
            </ol>
          </div>

          <div className={`ai-section deadline ${deadlineUrgent ? 'urgent' : ''}`}>
            <p className="ai-section-title">Respond within {analysis.deadline}</p>
          </div>

          <div className="ai-section docs">
            <p className="ai-section-title">Documents you will need:</p>
            <ul>
              {analysis.documents_needed.map((doc, i) => (
                <li key={i}>{doc}</li>
              ))}
            </ul>
          </div>

          {analysis.severity === 'High' && (
            <div className="ai-section consequence">
              <p className="ai-section-title">If you don't respond:</p>
              <p style={{ margin: 0 }}>{analysis.consequence}</p>
            </div>
          )}

          <div className="ai-action-buttons">
            <button className="dash-btn dash-btn-orange" type="button" onClick={openDraftModal}>
              Draft Response Letter ↗
            </button>
            <button className="dash-btn dash-btn-outline" type="button" onClick={handleDownloadAnalysis}>
              Download Analysis
            </button>
          </div>
        </div>
      )}

      {draftOpen && (
        <div className="ncf-modal-overlay" onClick={() => setDraftOpen(false)}>
          <div className="ncf-modal ai-draft-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="ncf-modal-title">Draft Response Letter</h3>

            {draftLoading && <p>Drafting with Claude...</p>}
            {draftError && <p className="dash-error">{draftError}</p>}
            {!draftLoading && !draftError && (
              <textarea className="ai-draft-text" readOnly value={draftLetter} />
            )}

            <div className="ncf-modal-actions">
              <button className="dash-btn cal-btn-grey" type="button" onClick={() => setDraftOpen(false)}>
                Close
              </button>
              {draftLetter && (
                <>
                  <button className="dash-btn dash-btn-outline" type="button" onClick={handleCopyLetter}>
                    {copied ? 'Copied ✓' : 'Copy to Clipboard'}
                  </button>
                  <button className="dash-btn dash-btn-orange" type="button" onClick={handleDownloadWord}>
                    Download as Word
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </PageLayout>
  );
}

export default AiNotice;
